import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import useCallApi from "../../hooks/useCallApi";
import { Routes } from "../../routes/Routes";
import { useAlert } from "../../context/AlertContext";
import { useFormik } from "formik";
import event from "../../validations/event";
import parkingValidationSchema from "../../validations/event/parking";
import EventApi from "../../api/event";
import price from "../../validations/event/price";
import { useContent } from "../../content/useContent";
import { useRTL } from "../../hooks/useLanguage/useRTL";

type DeletedImageType = "image" | "hero" | "logo" | "";

const useEventContainer = (id?: any, isEdit: any = false) => {
  const navigate = useNavigate();
  const { callApi, loading } = useCallApi();
  const { setSuccessMessage, setErrorMessage } = useAlert();
  const { schema, pages, general } = useContent();
  const { isRTL } = useRTL();

  const { events } = pages;
  const [pageLoading, setPageLoading] = useState(true);

  const [deletedImageType, setDeletedImageType] = useState<DeletedImageType>("");

  const [initialValuesEvent, setInitialValuesEvent] = useState({
    title: "",
    titleAr: "",
    details: "",
    detailsAr: "",
    longitude: 0,
    latitude: 0,
    POI: "",
    publishDate: new Date().toISOString(),
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    category: "",
    partnerCode: "",
    website: "",
    parking: [],
    onlyForTenants: false
  });

  const [images, setImages] = useState<any>([]);
  const [heroImage, setHeroImage] = useState<any>([]);
  const [logo, setLogo] = useState<any>([]);
  const [prices, setPrices] = useState<any>([]);

  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(5);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPriceDialog, setOpenPriceDialog] = useState(false);
  const [openDeletePriceDialog, setOpenDeletePriceDialog] = useState(false);
  
  const [parking, setParking] = useState<any>([]);
  const [openParkingDialog, setOpenParkingDialog] = useState(false);
  const [openDeleteParkingDialog, setOpenDeleteParkingDialog] = useState(false);
  const [selected, setSelected] = useState<any>(null);

  const handleOpenDeleteParking = (parking: any) => {
    setSelected(parking);
    setOpenDeleteParkingDialog(true);
  };

  const handleDeleteParking = () => {
    setSelected(null);
    setOpenDeleteParkingDialog(false);
    setParking(parking.filter((park: string) => park !== selected));
  };

  const handleOpenDeletePrice = (price: any) => {
    setSelected(price);
    setOpenDeletePriceDialog(true);
  };

  const handleDeletePrice = () => {
    if (selected && selected.id) {
      callApi(EventApi.deletePrice(selected.id), () => {
        setSelected(null);
        setOpenDeletePriceDialog(false);
        setSuccessMessage(events.deletePrice);
        setPrices(prices.filter((price: any) => price.id !== selected.id));
      });
    } else {
      setSelected(null);
      setOpenDeletePriceDialog(false);
      setSuccessMessage(events.deletePrice);
      setPrices(prices.filter((price: any) => price.title !== selected.title));
    }
  };

  useEffect(() => {
    if (id) {
      setPageLoading(true);

      const API = isEdit ? EventApi.getOneBoth(id) : EventApi.getOne(id);
      callApi(API, (event: any) => {
        if (isEdit) {
          setInitialValuesEvent({
            ...event,
            title: event.title.en,
            titleAr: event.title.ar,
            details: event.details.en,
            detailsAr: event.details.ar,
          });
        } else {
          setInitialValuesEvent(event);
        }

        setParking(event.parking);
        setPrices(event.prices);
        setImages(event.images);
        setCenter({ lat: event.latitude, lng: event.longitude });
        setHeroImage(event.heroImage ? [event.heroImage] : []);
        setLogo(event.logo ? [event.logo] : []);
        setPageLoading(false);
      });
    } else {
      setPageLoading(false);
    }
  }, [id, isRTL]);

  const handleDelete = () => {
    callApi(EventApi.delete(id), () => {
      setOpenDeleteDialog(false);
      navigate(Routes.events);
      setTimeout(
        () => setSuccessMessage("Event has been deleted successfully"),
        1000
      );
    });
  };

  const handleSubmit = (values: any) => {
    let noIdPrices = prices.map((p: any) => {
      return {
        title: p.title,
        price: p.price,
      };
    });

    let body = { ...values, prices: noIdPrices, parking };

    body.title = {
      en: values.title,
      ar: values.titleAr,
    };

    body.details = {
      en: values.details,
      ar: values.detailsAr,
    };

    delete body.titleAr;
    delete body.detailsAr;

    body.images = images.map((image: any) => {
      return { url: image.url, thumbnail: image.thumbnail };
    });
    body.heroImage = heroImage[0];
    body.logo = logo[0];

    delete body.id;
    delete body.createdAt;
    delete body.updatedAt;
    delete body.loyaltyCategory;

    if (values.POI === "") {
      delete body.POI;
    }

    const successMessage = id ? events.updateEvent : events.createEvent;

    if (id) {
      callApi(EventApi.update(id, body), () => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getEvent(id));
      });
    } else {
      callApi(EventApi.create(body), (result: any) => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getEvent(result.id));
      });
    }
  };

  const handleAddParking = (values: any, resetForm: any) => {
    if (parking.some((park: any) => park === values.parking)) {
      setErrorMessage(events.parkingExists);
    } else {
      setParking([...parking, values.parking]);
      setOpenParkingDialog(false);
      resetForm();
    }
  };

  const parkingFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      parking: "",
    },
    validationSchema: () => parkingValidationSchema(schema),
    onSubmit: (values: any, { resetForm }: any) =>
      handleAddParking(values, resetForm),
  });

  const eventFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesEvent,
    validationSchema: () => event(schema),
    onSubmit: (values: any) => handleSubmit(values),
  });

  const handleAddPrice = (values: any, resetForm: any) => {
    setPrices([
      ...prices,
      {
        title: { ar: values.titleAr, en: values.title },
        price: `${values.price}`,
      },
    ]);
    if (
      prices.some(
        (price: any) =>
          price.title.ar === values.titleAr || price.title.en === values.title
      )
    ) {
      setErrorMessage(events.priceExists);
    } else {
      setOpenPriceDialog(false);
      resetForm();
    }
  };
  const priceFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      titleAr: "",
      price: 0,
    },
    validationSchema: () => price(schema),
    onSubmit: (values: any, { resetForm }: any) =>
      handleAddPrice(values, resetForm),
  });

  const handleMarker = (e: any) => {
    eventFormik.setFieldValue("latitude", e.latLng.lat());
    eventFormik.setFieldValue("longitude", e.latLng.lng());
  };

  const handleSearch = (lat: any, lng: any) => {
    eventFormik.setFieldValue("latitude", lat);
    eventFormik.setFieldValue("longitude", lng);
  };

  const handleFile = (event: any) => {
    const files = event.target.files;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i]);
    }
    callApi(EventApi.upload(formData), async (data: any) => {
      setSuccessMessage(general.uploadImages);
      setImages([...images, ...data]);
    });
  };

  const handleHeroImage = (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("images", file);
    callApi(EventApi.upload(formData), async (data: any) => {
      setSuccessMessage(events.uploadHeroImage);
      setHeroImage(data);
    });
  };

  const handleLogo = (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("images", file);
    callApi(EventApi.upload(formData), async (data: any) => {
      setSuccessMessage(events.uploadLogo);
      setLogo(data);
    });
  };

  const handleOpenDeleteImage = (image: any) => {
    setOpenDeleteDialog(true);
    setSelected(image);
    setDeletedImageType("image");
  };

  const handleOpenDeleteHeroImage = (image: any) => {
    setOpenDeleteDialog(true);
    setSelected(image);
    setDeletedImageType("hero");
  };

  const handleOpenDeleteLogo = (image: any) => {
    setOpenDeleteDialog(true);
    setSelected(image);
    setDeletedImageType("logo");
  };

  const handleDeleteImage = () => {
    if (selected.id) {
      callApi(EventApi.deleteImage(selected.id), async (data: any) => {
        setSuccessMessage(general.deleteImage);
        if (deletedImageType === "hero") {
          setHeroImage([]);
        } else if (deletedImageType === "logo") {
          setLogo([]);
        } else {
          setImages(images.filter((image: any) => image.id !== selected.id));
        }

        setSelected(null);
        setOpenDeleteDialog(false);
      });
    } else {
      callApi(
        EventApi.deleteImageSource({ url: selected.url }),
        async (data: any) => {
          setSuccessMessage(general.deleteImage);
          if (deletedImageType === "hero") {
            setHeroImage([]);
          } else if (deletedImageType === "logo") {
            setLogo([]);
          } else {
            setImages(images.filter((image: any) => image.url !== selected.url));
          }

          setSelected(null);
          setOpenDeleteDialog(false);
        }
      );
    }
  };

  return {
    event: initialValuesEvent,
    pageLoading,

    eventFormik,
    center,
    handleMarker,
    handleSearch,
    setCenter,
    zoom,
    setZoom,

    handleDelete,
    openDeleteDialog,
    setOpenDeleteDialog,
    loading,
    prices,
    priceFormik,
    setOpenPriceDialog,
    openPriceDialog,

    handleOpenDeletePrice,
    setOpenDeletePriceDialog,
    openDeletePriceDialog,
    handleDeletePrice,

    handleFile,
    handleOpenDeleteImage,
    handleDeleteImage,
    images,

    handleHeroImage,
    handleOpenDeleteHeroImage,
    heroImage,

    handleLogo,
    handleOpenDeleteLogo,
    logo,

    parkingFormik,
    setOpenParkingDialog,
    openParkingDialog,
    handleOpenDeleteParking,
    parking,
    handleDeleteParking,
    openDeleteParkingDialog,
    setOpenDeleteParkingDialog,
    selected,
  };
};

export default useEventContainer;
